import { type FC, useMemo, useState } from "react";
import { Group, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/nb";
import "dayjs/locale/en-gb";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import ModalHeader from "@/components/atoms/ModalHeader";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import Checkbox from "@/components/atoms/Checkbox";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { classNames } from "@/utils/Classnames";
import { ReceptionSlot } from "@/types";
import { useGetFractionWindows, useGetReceptionCalendarSlots } from "@/services/ReceptionSettings";
import { useGetAllSalesItemsByLanguageId } from "@/services/Product";
import { useAuth } from "@/contexts/AuthContext";
import {
  resolveAcceptedFractionNames,
  resolveActiveFractionWindow,
  windowAcceptsFraction,
} from "@/utils/ReceptionFractionWindows";
import {
  buildWeekDays,
  getVisibleTimeRows,
  groupSlotsByDate,
  isSlotFull,
  isSlotPartial,
  isSlotPast,
  startOfWeek,
} from "@/utils/ReceptionSlotGrid";
import SlotCard, { SlotState } from "./SlotCard";
import gridStyles from "@/components/molecules/ReceptionSlotGrid/styles.module.css";
import styles from "./styles.module.css";

dayjs.extend(isoWeek);

interface Props {
  opened: boolean;
  receiverCompanyId: number;
  fractionId: number | null;
  onConfirm: (slot: ReceptionSlot) => void;
  onClose: () => void;
}

const DeliverySlotPicker: FC<Props> = ({ opened, receiverCompanyId, fractionId, onConfirm, onClose }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const dayjsLocale = i18n.language.startsWith("no") ? "nb" : "en-gb";

  const [weekStart, setWeekStart] = useState(() => startOfWeek(dayjs()));
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  // Checked (default) = filtered to the order's fraction; unchecking reveals every slot.
  const [filterToFraction, setFilterToFraction] = useState(true);
  const showAll = !filterToFraction;

  const fromDate = weekStart.format("YYYY-MM-DD");
  const toDate = weekStart.add(6, "day").format("YYYY-MM-DD");

  const { data: slots = [] } = useGetReceptionCalendarSlots(receiverCompanyId, fromDate, toDate, null, {
    enabled: opened && !!receiverCompanyId,
  });

  const { data: fractionWindows } = useGetFractionWindows(receiverCompanyId, {
    enabled: opened && !!receiverCompanyId,
  });

  const { data: salesItems = [] } = useGetAllSalesItemsByLanguageId(user?.language?.id ?? 1, { enabled: opened });

  const fractionNameMap = useMemo(
    () => new Map(salesItems.map((item) => [item.salesItemId, item.salesItemTranslated])),
    [salesItems]
  );

  const fractionName = fractionId != null ? (fractionNameMap.get(fractionId) ?? String(fractionId)) : null;

  const days = useMemo(() => buildWeekDays(weekStart, dayjsLocale), [weekStart, dayjsLocale]);
  const slotsByDate = useMemo(() => groupSlotsByDate(slots), [slots]);

  const dayInfo = useMemo(
    () =>
      new Map(
        days.map((day) => {
          const activeWindow = resolveActiveFractionWindow(day.iso, fractionWindows ?? []);
          const acceptsFraction = fractionWindows !== undefined && windowAcceptsFraction(activeWindow, fractionId);
          const display = !activeWindow
            ? fractionWindows === undefined
              ? "—"
              : t("orders:deliverySlotPicker.allFractions")
            : activeWindow.closed
              ? "—"
              : activeWindow.acceptAll
                ? t("orders:deliverySlotPicker.allFractions")
                : acceptsFraction
                  ? (fractionName ?? "—")
                  : "—";
          const acceptedNames = activeWindow ? resolveAcceptedFractionNames(activeWindow, fractionNameMap) : [];
          const full = !activeWindow
            ? fractionWindows === undefined
              ? "—"
              : t("orders:deliverySlotPicker.allFractions")
            : activeWindow.closed
              ? "—"
              : activeWindow.acceptAll
                ? t("orders:deliverySlotPicker.allFractions")
                : acceptedNames.length > 0
                  ? acceptedNames.join(", ")
                  : "—";
          return [day.iso, { activeWindow, acceptsFraction, display, full }];
        })
      ),
    [days, fractionWindows, fractionId, fractionName, fractionNameMap, t]
  );

  const visibleDays = useMemo(
    () => (showAll || fractionId == null ? days : days.filter((day) => dayInfo.get(day.iso)?.acceptsFraction)),
    [days, dayInfo, showAll, fractionId]
  );

  const visibleSlots = useMemo(
    () => visibleDays.flatMap((day) => slotsByDate.get(day.iso) ?? []),
    [visibleDays, slotsByDate]
  );

  const timeRows = useMemo(() => getVisibleTimeRows(visibleSlots), [visibleSlots]);

  const selectedSlot = slots.find((s) => s.id === selectedSlotId) ?? null;

  const isPrevDisabled = weekStart.isSame(startOfWeek(dayjs()), "day");

  const localizedWeekStart = weekStart.locale(dayjsLocale);
  const localizedWeekEnd = localizedWeekStart.add(6, "day");
  const weekLabel = `${t("common:week")} ${localizedWeekStart.isoWeek()} · ${
    localizedWeekStart.month() === localizedWeekEnd.month()
      ? `${localizedWeekStart.format("D")}–${localizedWeekEnd.format("D MMM YYYY")}`
      : `${localizedWeekStart.format("D MMM")}–${localizedWeekEnd.format("D MMM YYYY")}`
  }`;

  const slotState = (slot: ReceptionSlot, day: (typeof days)[number]): SlotState => {
    const accepts = dayInfo.get(day.iso)?.acceptsFraction ?? fractionId == null;
    if (isSlotPast(slot) || !accepts) return "closed";
    if (isSlotFull(slot)) return "full";
    if (isSlotPartial(slot)) return "partial";
    return "open";
  };

  const handleConfirm = () => {
    if (selectedSlot) onConfirm(selectedSlot);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      closeOnClickOutside={false}
      title={<ModalHeader title={t("orders:chooseDeliveryTimeSlot")} svgAsset="schedule" />}
    >
      <Stack gap="md" py="20px">
        {fractionName && (
          <Group className={styles.banner} wrap="nowrap">
            <Checkbox checked={filterToFraction} onChange={(e) => setFilterToFraction(e.currentTarget.checked)} />
            <Text size="sm" strong className={styles.bannerFraction}>
              {fractionName}
            </Text>
            <Text size="xs" color="secondary">
              {t("orders:deliverySlotPicker.banner", { fractionName })}
            </Text>
          </Group>
        )}

        <div className={classNames(gridStyles.navRow, gridStyles.navRowPadded)}>
          <div className={gridStyles.navBtnGroup}>
            <button
              type="button"
              className={gridStyles.navBtn}
              aria-label={t("containers:schedule.prevWeek")}
              disabled={isPrevDisabled}
              onClick={() => !isPrevDisabled && setWeekStart((w) => w.subtract(7, "day"))}
            >
              <SVGAsset name="chevron-right" width={12} height={12} style={{ transform: "rotate(180deg)" }} />
            </button>
            <button
              type="button"
              className={gridStyles.navBtn}
              aria-label={t("containers:schedule.nextWeek")}
              onClick={() => setWeekStart((w) => w.add(7, "day"))}
            >
              <SVGAsset name="chevron-right" width={12} height={12} />
            </button>
          </div>

          <Text size="sm" strong display="technical" className={gridStyles.weekLabel}>
            {weekLabel}
          </Text>

          <div className={gridStyles.legend}>
            <span className={gridStyles.legendItem}>
              <span className={classNames(gridStyles.legendSwatch, gridStyles.legendSwatchOpen)} />
              <Text size="xs" color="secondary" display="technical" tt="uppercase">
                {t("orders:receptionSlots.legend.available")}
              </Text>
            </span>
            <span className={gridStyles.legendItem}>
              <span className={classNames(gridStyles.legendSwatch, gridStyles.legendSwatchPartial)} />
              <Text size="xs" color="secondary" display="technical" tt="uppercase">
                {t("orders:receptionSlots.legend.partial")}
              </Text>
            </span>
            <span className={gridStyles.legendItem}>
              <span className={classNames(gridStyles.legendSwatch, gridStyles.legendSwatchFull)} />
              <Text size="xs" color="secondary" display="technical" tt="uppercase">
                {t("orders:receptionSlots.legend.full")}
              </Text>
            </span>
            <span className={gridStyles.legendItem}>
              <span className={classNames(gridStyles.legendSwatch, gridStyles.legendSwatchClosed)} />
              <Text size="xs" color="secondary" display="technical" tt="uppercase">
                {t("orders:receptionSlots.legend.closed")}
              </Text>
            </span>
          </div>
        </div>

        <div
          className={gridStyles.grid}
          style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(104px, 1fr))` }}
        >
          <div className={gridStyles.dayHeadRow}>
            <div className={gridStyles.timeAxisHead} />
            {days.map((day) => (
              <div key={day.iso} className={classNames(gridStyles.dayHead, day.isToday ? gridStyles.dayHeadToday : "")}>
                <Text size="xs" display="technical" tt="uppercase" color="secondary" className={gridStyles.dayWeekday}>
                  {day.weekdayLabel}
                </Text>
                <Text size="md" strong className={gridStyles.dayNumber}>
                  {day.dayNumber}
                </Text>
                <Text size="xs" display="technical" tt="uppercase" color="secondary" className={gridStyles.dayStatus}>
                  {day.isPast
                    ? t("orders:receptionSlots.legend.past")
                    : day.isToday
                      ? t("orders:deliverySlotPicker.today")
                      : t("orders:deliverySlotPicker.slotCount", {
                          count: (slotsByDate.get(day.iso) ?? []).length,
                        })}
                </Text>
              </div>
            ))}
          </div>

          {timeRows.map((time) => (
            <div key={time} className={gridStyles.dayHeadRow}>
              <div className={gridStyles.timeAxisCell} data-testid="time-axis-label">
                <Text size="xs" display="technical" color="secondary">
                  {time.slice(0, 5)}
                </Text>
              </div>
              {days.map((day) => {
                const slot = (slotsByDate.get(day.iso) ?? []).find((s) => s.startTime === time);
                if (!slot) {
                  return (
                    <div
                      key={day.iso}
                      data-testid="day-cell-closed"
                      className={classNames(gridStyles.dayCell, gridStyles.dayCellClosed)}
                    />
                  );
                }
                const info = dayInfo.get(day.iso);
                return (
                  <div key={day.iso} className={gridStyles.dayCell}>
                    <SlotCard
                      slot={slot}
                      state={slotState(slot, day)}
                      fractionLabel={info?.display ?? "—"}
                      fractionLabelFull={info?.full ?? "—"}
                      selected={slot.id === selectedSlotId}
                      onSelect={() => setSelectedSlotId(slot.id)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {timeRows.length === 0 && (
          <Text size="sm" color="secondary" ta="center" className={gridStyles.emptyState}>
            {t("orders:noSlotsAvailable")}
          </Text>
        )}
      </Stack>

      <ModalCTAs>
        <div className={styles.footer}>
          <Text size="xs" color="secondary" className={styles.footerHint}>
            {selectedSlot
              ? t("orders:selectedSlot", {
                  time: `${dayjs(`${selectedSlot.slotDate}T${selectedSlot.startTime}`).format("DD.MM.YYYY HH:mm")}`,
                })
              : t("orders:deliverySlotPicker.pickSlotToContinue")}
          </Text>
          <div className={styles.footerActions}>
            <CTA intent="secondary" size="lg" onClick={onClose}>
              {t("actions:cancel")}
            </CTA>
            <CTA intent="primary" size="lg" disabled={!selectedSlot} onClick={handleConfirm}>
              {t("orders:confirmSlot")}
            </CTA>
          </div>
        </div>
      </ModalCTAs>
    </Modal>
  );
};

export default DeliverySlotPicker;
